/**
 * EditorObjectList: DOM list of the current scene's objects of the ACTIVE
 * config item's type (the left-panel 配置项目 selected in EditorState).
 *
 * Object-type items: the 新增 button at the top starts placement for that
 * type; click a row to select (synced with EditorState); right-click deletes.
 * Scene-config items show a summary row instead: 出生区 (center/size, click to
 * pseudo-select → gizmo drag) and 天空盒 (desktop/mobile textures). Re-renders
 * on any state change. The list reads from the live `SceneObjectManager` so it
 * always reflects what's in the world.
 */

import type { EditorState } from './EditorState';
import { SPAWN_AREA_CONFIG_ID, SKYBOX_CONFIG_ID, isSceneConfigId } from './EditorState';
import type { EditorAsset } from './EditorAssetLibrary';
import type { SceneObjectManager } from '../scene/SceneObjectManager';

export interface ObjectListCallbacks {
    select(id: string): void;
    delete(id: string): void;
    /** 新增：start placement for the active config item's type. */
    add(): void;
    /** 创建出生区：re-center the spawn area on the player's current pose
     *  (the 出生区 config item's create action, shown when unconfigured). */
    createSpawnArea(): void;
    /** 选中出生区（伪选中 → gizmo 拖拽）：the 出生区 summary row click. */
    selectSpawnArea(): void;
}

export class EditorObjectList {
    private _mount: HTMLElement;
    private _state: EditorState;
    private _getMgr: () => SceneObjectManager | null;
    /** The active left-panel config item (or null before one is chosen). */
    private _getAsset: () => EditorAsset | null;
    /** The current spawn area (null = unconfigured). */
    private _getSpawnArea: () => { center: [number, number, number]; size: [number, number] } | null;
    /** Short labels of the configured desktop/mobile skyboxes. */
    private _getSkybox: () => { desktop: string; mobile: string } | null;
    /** Registry display name for a scene id (portal rows append the target). */
    private _getSceneName: (id: string) => string | undefined;
    private _cb: ObjectListCallbacks;
    private _unsubscribe: () => void;

    constructor(
        mount: HTMLElement,
        state: EditorState,
        getMgr: () => SceneObjectManager | null,
        cb: ObjectListCallbacks,
        getAsset: () => EditorAsset | null,
        getSpawnArea: () => { center: [number, number, number]; size: [number, number] } | null,
        getSkybox: () => { desktop: string; mobile: string } | null,
        getSceneName: (id: string) => string | undefined
    ) {
        this._mount = mount;
        this._state = state;
        this._getMgr = getMgr;
        this._getAsset = getAsset;
        this._getSpawnArea = getSpawnArea;
        this._getSkybox = getSkybox;
        this._getSceneName = getSceneName;
        this._cb = cb;
        this._unsubscribe = state.subscribe(() => this._render());
        this._render();
    }

    private _render(): void {
        const mgr = this._getMgr();
        const asset = this._getAsset();
        const sel = this._state.selectedId;
        this._mount.innerHTML = '';

        if (!asset) {
            // 场景级配置项（出生区/天空盒）：显示对应配置数据。
            if (this._state.selectedAssetId === SPAWN_AREA_CONFIG_ID) {
                this._renderSpawnAreaRows(sel);
                return;
            }
            if (this._state.selectedAssetId === SKYBOX_CONFIG_ID) {
                this._renderSkyboxRows();
                return;
            }
            const empty = document.createElement('div');
            empty.className = 'ep-empty';
            empty.textContent = isSceneConfigId(this._state.selectedAssetId)
                ? '该项为场景级配置，请在右侧「属性」面板中设置'
                : '点击左侧配置项目，查看对应类型的对象';
            this._mount.appendChild(empty);
            return;
        }

        // 新增 button: places a fresh object of the active config item's type.
        const addBtn = document.createElement('button');
        addBtn.type = 'button';
        addBtn.className = 'ol-add';
        addBtn.textContent = `＋ 新增${asset.name}`;
        addBtn.title = `在场景中放置一个新的「${asset.name}」`;
        addBtn.addEventListener('click', () => this._cb.add());
        this._mount.appendChild(addBtn);

        const objects = (mgr ? mgr.objects : []).filter((o) => o.type === asset.objectType);
        if (!objects.length) {
            const empty = document.createElement('div');
            empty.className = 'ep-empty';
            empty.textContent = `暂无${asset.name}对象，点击上方「新增」放置`;
            this._mount.appendChild(empty);
            return;
        }

        for (let i = 0; i < objects.length; i++) {
            const o = objects[i];
            const row = document.createElement('div');
            row.className = 'ol-row' + (o.id === sel ? ' selected' : '') + (o.enabled === false ? ' disabled' : '');
            row.title = `${o.type} · ${o.id}${o.enabled === false ? ' · 已关闭（属性面板可重新开启）' : ''}`;
            const typeTag = document.createElement('span');
            typeTag.className = 'ol-type';
            typeTag.textContent = asset.name;
            const name = document.createElement('span');
            name.className = 'ol-name';
            // 序号 + 名称；传送门行追加目标场景名（如「传送门-梅花山」）。
            let label = `${i + 1}. ${o.name}`;
            if (o.type === 'portal' && o.portal?.targetScene) {
                const target = this._getSceneName(o.portal.targetScene) ?? o.portal.targetScene;
                label = `${i + 1}. ${o.name}-${target}`;
            }
            if (o.enabled === false) label += '（已关闭）';
            name.textContent = label;
            const del = document.createElement('button');
            del.className = 'ol-del';
            del.textContent = '×';
            del.title = '删除';
            del.addEventListener('click', (e) => {
                e.stopPropagation();
                this._cb.delete(o.id);
            });
            row.appendChild(typeTag);
            row.appendChild(name);
            row.appendChild(del);

            row.addEventListener('click', () => this._cb.select(o.id));

            this._mount.appendChild(row);
        }
    }

    /** 出生区 config item rows: a summary row (click → pseudo-select, gizmo
     *  drag) when configured; the 创建出生区 button + hint when not. */
    private _renderSpawnAreaRows(sel: string | null): void {
        const area = this._getSpawnArea();
        if (!area) {
            const createBtn = document.createElement('button');
            createBtn.type = 'button';
            createBtn.className = 'ol-add';
            createBtn.textContent = '创建出生区';
            createBtn.title = '以当前位置为矩形中心创建出生区（4×4）';
            createBtn.addEventListener('click', () => this._cb.createSpawnArea());
            this._mount.appendChild(createBtn);
            const empty = document.createElement('div');
            empty.className = 'ep-empty';
            empty.textContent = '尚未配置出生区，点击上方「创建出生区」';
            this._mount.appendChild(empty);
            return;
        }

        const row = document.createElement('div');
        row.className = 'ol-row' + (sel === SPAWN_AREA_CONFIG_ID ? ' selected' : '');
        row.title = `中心 (${area.center.map((v) => +v.toFixed(2)).join(', ')}) · 点击选中后可在场景内拖拽（W移动 / R缩放）`;
        const typeTag = document.createElement('span');
        typeTag.className = 'ol-type';
        typeTag.textContent = '配置';
        const name = document.createElement('span');
        name.className = 'ol-name';
        name.textContent = `出生区 ${area.size[0].toFixed(1)}×${area.size[1].toFixed(1)}`;
        row.appendChild(typeTag);
        row.appendChild(name);
        row.addEventListener('click', () => this._cb.selectSpawnArea());
        this._mount.appendChild(row);

        const hint = document.createElement('div');
        hint.className = 'ep-empty';
        hint.textContent = '点击行选中后可在场景内拖拽 · 详细数据在右侧「属性」面板';
        this._mount.appendChild(hint);
    }

    /** 天空盒 config item rows: a summary row of the configured desktop +
     *  mobile skybox labels (edit in the property panel). */
    private _renderSkyboxRows(): void {
        const sky = this._getSkybox();
        const row = document.createElement('div');
        row.className = 'ol-row';
        row.title = '在右侧「属性」面板中选择贴图、调整强度与旋转';
        const typeTag = document.createElement('span');
        typeTag.className = 'ol-type';
        typeTag.textContent = '配置';
        const name = document.createElement('span');
        name.className = 'ol-name';
        name.textContent = sky
            ? `桌面 ${sky.desktop} · 手机 ${sky.mobile}`
            : '未配置天空盒';
        row.appendChild(typeTag);
        row.appendChild(name);
        this._mount.appendChild(row);

        const hint = document.createElement('div');
        hint.className = 'ep-empty';
        hint.textContent = '贴图与参数在右侧「属性」面板中配置';
        this._mount.appendChild(hint);
    }

    destroy(): void { this._unsubscribe(); this._mount.innerHTML = ''; }
}
